/**
 * THALIA core — wireframe icosahedron, particle shell, three rotating rings.
 * Pulses on key buys (pulseKey changes). In the final 5 minutes it shifts toward
 * the urgency color, spins faster and gains a heartbeat that quickens toward zero.
 */
import { useEffect, useRef, type MutableRefObject } from 'react';
import * as THREE from 'three';
import type { UrgencyState } from './urgency';
import { prefersReducedMotion } from './urgency';

export default function HoloCore({ urgencyRef, pulseKey }: { urgencyRef: MutableRefObject<UrgencyState>; pulseKey: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pulse = useRef(0);

  useEffect(() => { if (pulseKey > 0) pulse.current = 1; }, [pulseKey]);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas: cv, alpha: true, antialias: true });
    } catch {
      return; // no WebGL — the SVG gauge still works
    }
    const RM = prefersReducedMotion();
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    const scene = new THREE.Scene();
    const cam = new THREE.PerspectiveCamera(40, 1, 0.1, 100);
    cam.position.z = 7.2;
    const g = new THREE.Group();
    scene.add(g);

    const icoMat = new THREE.MeshBasicMaterial({ color: 0x7f8cf0, wireframe: true, transparent: true, opacity: 0.2 });
    const ico = new THREE.Mesh(new THREE.IcosahedronGeometry(1.15, 1), icoMat);
    g.add(ico);

    const N = 600;
    const pp = new Float32Array(N * 3), cc = new Float32Array(N * 3), base = new Float32Array(N * 3);
    const A = new THREE.Color(0x843ee1), B = new THREE.Color(0xf058f6), Q = new THREE.Color();
    for (let i = 0; i < N; i++) {
      const u = Math.random() * 2 - 1, t = Math.random() * 6.283, r = 1.5 + Math.random() * 0.25, s = Math.sqrt(1 - u * u);
      pp.set([r * s * Math.cos(t), r * s * Math.sin(t), r * u], i * 3);
      Q.copy(A).lerp(B, Math.random());
      base.set([Q.r, Q.g, Q.b], i * 3);
    }
    cc.set(base);
    const pg = new THREE.BufferGeometry();
    pg.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const colAttr = new THREE.BufferAttribute(cc, 3);
    pg.setAttribute('color', colAttr);
    const ptsMat = new THREE.PointsMaterial({ vertexColors: true, size: 0.032, transparent: true, opacity: 0.55 });
    const pts = new THREE.Points(pg, ptsMat);
    g.add(pts);

    const ringBase = [0x46def8, 0x843ee1, 0xf058f6].map(c => new THREE.Color(c));
    const rings: THREE.Mesh[] = [];
    const ringMats: THREE.MeshBasicMaterial[] = [];
    ([[2.05, 0.012, 0.9, 0], [2.3, 0.006, 1.2, 0.6], [2.55, 0.01, 0.5, -0.4]] as const).forEach(([r, w, tx, ty], i) => {
      const mat = new THREE.MeshBasicMaterial({ color: ringBase[i].clone(), transparent: true, opacity: 0.45 });
      const m = new THREE.Mesh(new THREE.TorusGeometry(r, w, 6, 160), mat);
      m.rotation.set(tx, ty, 0);
      const mk = new THREE.Mesh(new THREE.SphereGeometry(0.05, 10, 10), new THREE.MeshBasicMaterial({ color: i === 2 ? 0x46def8 : 0xf058f6 }));
      mk.position.x = r;
      m.add(mk);
      g.add(m);
      rings.push(m);
      ringMats.push(mat);
    });

    const size = () => {
      const w = cv.clientWidth || 400, h = cv.clientHeight || 400;
      renderer.setSize(w, h, false);
      cam.aspect = w / h;
      cam.updateProjectionMatrix();
    };
    size();
    window.addEventListener('resize', size);

    const icoBase = new THREE.Color(0x7f8cf0), red = new THREE.Color(), tmp = new THREE.Color();
    let raf = 0, last = 0, beatPhase = 0, lastLevel = -1;
    const bmRed = new THREE.Color(0xe0284f);
    const loop = (ts: number) => {
      raf = requestAnimationFrame(loop);
      if (ts - last < 33) return; // ~30fps
      const dt = Math.min(0.1, (ts - last) / 1000 || 0.033);
      last = ts;
      if (document.hidden) return;
      const u = urgencyRef.current;
      const BM = document.body.classList.contains('blood-moon');
      const L = u.active ? u.level : 0;
      const k = RM ? 0 : 1;
      const spin = 1 + L * 3; // spins faster toward zero
      g.rotation.y += 0.003 * k * spin;
      ico.rotation.x += 0.002 * k * spin;
      rings.forEach((r, i) => (r.rotation.z += (i % 2 ? -0.004 : 0.003) * k * spin));

      // heartbeat: ~0.6Hz at 5:00 → ~2.5Hz at 0:00
      if ((u.active || BM) && !RM) {
        beatPhase += dt * (0.6 + L * 1.9);
        if (beatPhase >= 1) { beatPhase -= 1; pulse.current = Math.max(pulse.current, 0.35 + L * 0.45); }
      }
      pulse.current *= 0.94;
      const p = pulse.current;
      ico.scale.setScalar(1 + p * 0.12);
      icoMat.opacity = 0.2 + p * 0.4 + L * 0.15;
      ptsMat.opacity = 0.55 + p * 0.35;

      // shift palette toward urgency color
      const key = L + (BM ? 10 : 0);
      if (Math.abs(key - lastLevel) > 0.002) {
        lastLevel = key;
        if (BM) red.copy(bmRed); else red.setRGB(u.rgb[0] / 255, u.rgb[1] / 255, u.rgb[2] / 255);
        const mix = BM ? 0.85 : L > 0 ? 0.35 + L * 0.6 : 0;
        icoMat.color.copy(icoBase).lerp(red, mix);
        ringMats.forEach((m, i) => m.color.copy(ringBase[i]).lerp(red, mix));
        for (let i = 0; i < N; i++) {
          tmp.setRGB(base[i * 3], base[i * 3 + 1], base[i * 3 + 2]).lerp(red, mix * 0.8);
          cc[i * 3] = tmp.r; cc[i * 3 + 1] = tmp.g; cc[i * 3 + 2] = tmp.b;
        }
        colAttr.needsUpdate = true;
      }
      renderer.render(scene, cam);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', size);
      scene.traverse(o => {
        const m = o as THREE.Mesh;
        m.geometry?.dispose?.();
        const mat = m.material as THREE.Material | undefined;
        mat?.dispose?.();
      });
      renderer.dispose();
    };
  }, [urgencyRef]);

  return <canvas ref={canvasRef} className="holo-core3d" aria-hidden="true" />;
}
